import { useTranslation } from 'react-i18next';
import { Play, Square } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useAuthStore } from '@/core/stores/auth.store';
import {
  useActiveTimer,
  useStartTimer,
  useStopTimer,
  TimerDisplay,
  formatTrackedHours,
} from '@/features/time-tracking/public';

interface TaskTimerControlProps {
  taskId: string;
  assigneeId?: string | null;
  totalTrackedHours?: number | null;
}

export function TaskTimerControl({ taskId, assigneeId, totalTrackedHours }: TaskTimerControlProps) {
  const { t } = useTranslation('tasks');
  const currentUserId = useAuthStore((s) => s.user?.id);
  const { data: activeTimer, isLoading } = useActiveTimer();
  const startTimer = useStartTimer();
  const stopTimer = useStopTimer();

  const isRunning = !!activeTimer?.id && activeTimer.task?.id === taskId;
  const isPending = startTimer.isPending || stopTimer.isPending;
  // Time tracking is per-person — only the assignee's clock can be started/stopped
  // on this task, otherwise one person's hours would get logged under another's.
  const isOwnTask = !!assigneeId && assigneeId === currentUserId;

  function handleToggle() {
    if (isRunning) {
      stopTimer.mutate(taskId);
    } else {
      startTimer.mutate(taskId);
    }
  }

  if (isLoading) {
    return (
      <Skeleton className="h-4 w-16 rounded" aria-hidden="true" data-testid="tasks-timer-loading" />
    );
  }

  return (
    <div className="flex items-center gap-1" data-testid="tasks-timer-control">
      {isRunning ? (
        <TimerDisplay
          startTime={activeTimer.startTime}
          className="text-sm font-medium tabular-nums text-foreground"
        />
      ) : (
        <span className="text-sm tabular-nums text-muted-foreground">
          {formatTrackedHours(totalTrackedHours)}
        </span>
      )}

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label={isRunning ? t('timer.stop') : t('timer.start')}
            data-testid={isRunning ? 'tasks-timer-stop-btn' : 'tasks-timer-start-btn'}
            disabled={isPending || !isOwnTask}
            onClick={handleToggle}
            aria-busy={isPending}
          >
            {isPending ? (
              <span
                className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
                aria-hidden="true"
              />
            ) : isRunning ? (
              <Square className="h-3.5 w-3.5 fill-current text-green-600" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isOwnTask ? (isRunning ? t('timer.stop') : t('timer.start')) : t('timer.assigneeOnly')}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
